/**
 * Canonical Swedish postal-address (postadress) formatter.
 *
 * Prefill/autofill composes a postadress from its parts and writes the
 * result into `contract.values`. The rendered form is a **single line**
 * with the street and the "postnummer ort" segment separated by a comma:
 *
 *   "Ångvägen 3, 114 44 Stockholm"
 *
 * (An optional `line2` — c/o etc. — sits between the two as its own
 * comma-separated segment.) This is the one place the format lives;
 * every composition site (property link, party autofill, import extract)
 * delegates here so the postadress reads identically across templates.
 *
 * The freetext postadress fields themselves are untouched — a user can
 * still type whatever they like, including line breaks. This only shapes
 * what prefill writes.
 */
export function composePostadress(parts: {
  /** Address line 1 — street + house number. */
  street?: string | null;
  /** Optional second line (c/o, careof) placed between street and postal. */
  line2?: string | null;
  postalCode?: string | null;
  ort?: string | null;
}): string | null {
  const street = parts.street?.trim() || null;
  const line2 = parts.line2?.trim() || null;
  const postalOrt =
    [parts.postalCode?.trim(), parts.ort?.trim()].filter(Boolean).join(' ').trim() ||
    null;
  const segments = [street, line2, postalOrt].filter(Boolean);
  return segments.length === 0 ? null : segments.join(', ');
}

// Swedish postal code: 3 digits + optional space + 2 digits.
const SWEDISH_POSTAL_RE = /\b(\d{3})\s?(\d{2})\b/;

/** Return a Swedish postal code in the canonical `NNN NN` form, or the
 *  trimmed input unchanged when it isn't a 5-digit code. */
export function normalisePostalCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 5) return raw.trim() || null;
  return `${digits.slice(0, 3)} ${digits.slice(3)}`;
}

/**
 * Inverse of {@link composePostadress}: best-effort split of a free-form
 * Swedish address into `{ street, postalCode, ort }`.
 *
 * Mirrors the doc-parser's Python `_split_address` (same postal regex,
 * same separator trimming) so that splitting a **party** address on import
 * behaves identically to the **property** address split the extractor
 * already performs. The LLM emits a party's address as one free-text
 * string (`ExtractedParty.address`); this is where it becomes the discrete
 * `address_line1` / `postal_code` / `city` columns a `contract_parties`
 * row (and the postadress autofill mirror) expects.
 *
 * When no Swedish postal code is present, the whole input is kept as
 * `street` — losing the line entirely would be worse than an imperfect
 * split.
 */
export function parsePostadress(raw: string | null | undefined): {
  street: string | null;
  postalCode: string | null;
  ort: string | null;
} {
  const text = (raw ?? '').trim();
  if (!text) return { street: null, postalCode: null, ort: null };
  const m = SWEDISH_POSTAL_RE.exec(text);
  if (!m) return { street: text || null, postalCode: null, ort: null };
  const postalCode = normalisePostalCode(m[0]);
  const before = text.slice(0, m.index).replace(/[\s,;\n\t-]+$/, '');
  const after = text.slice(m.index + m[0].length).replace(/^[\s,;\n\t-]+/, '');
  return { street: before || null, postalCode, ort: after || null };
}
